/**
 * observability — telemetry wiring for errors and product analytics.
 *
 * Three independent destinations, each activated on its own terms:
 *
 *   • Karr's own DB (always, in production) → reportError() writes to the
 *     `client_errors` table via the log_client_error() RPC. Vendor-free,
 *     zero-cost crash reporting owned entirely by Karr.
 *   • Sentry     (VITE_SENTRY_DSN)  → reportError() ALSO to Sentry, if set.
 *   • PostHog    (VITE_POSTHOG_KEY) → reportEvent() product-funnel analytics.
 *                (VITE_POSTHOG_HOST) — defaults to https://us.i.posthog.com
 *
 * The error sink is installed unconditionally, so production crashes are always
 * captured somewhere even with no external provider configured. Sentry/PostHog
 * are loaded via dynamic import so the app carries no analytics weight until
 * their keys are present.
 */

import { setTelemetrySink } from './telemetry';
import { supabase } from '@/db/supabase';

/* eslint-disable @typescript-eslint/no-explicit-any */

// Session guards so a crash loop can't spam the error table: one row per
// distinct error, capped per session.
const seenErrors = new Set<string>();
let errorsLogged = 0;
const MAX_ERRORS_PER_SESSION = 50;

function activeOrgId(): string | null {
    try {
        // Same key useAuth persists the active org under (avoids importing the
        // store here, which would create a circular dependency).
        return localStorage.getItem('karr_active_org');
    } catch {
        return null;
    }
}

/** Persist a caught error to Karr's own client_errors table. Never throws, never recurses. */
async function logErrorToDb(error: unknown, context?: Record<string, unknown>): Promise<void> {
    try {
        if (errorsLogged >= MAX_ERRORS_PER_SESSION) return;
        const err =
            error instanceof Error
                ? error
                : new Error(typeof error === 'string' ? error : JSON.stringify(error));
        // officewatch (7 Oct 2026): the browser's "ResizeObserver loop completed
        // with undelivered notifications" is a benign layout warning, not a
        // crash. It was 28 of 33 rows in the 48-hour digest and buried the real
        // ones, and a digest nobody can read is a digest nobody reads.
        if (/ResizeObserver loop/i.test(err.message)) return;
        const kind = ((context?.kind ?? context?.source) as string | undefined) ?? null;
        const dedupeKey = `${err.message}|${kind ?? ''}`;
        if (seenErrors.has(dedupeKey)) return; // one row per distinct error per session
        seenErrors.add(dedupeKey);
        errorsLogged++;
        await supabase.rpc('log_client_error', {
            p_message: err.message || 'Unknown error',
            p_stack: err.stack ?? null,
            p_kind: kind,
            p_url: typeof window !== 'undefined' ? window.location.href : null,
            p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            p_context: (context ?? null) as any,
            p_org_id: activeOrgId(),
            p_release: (import.meta.env.VITE_RELEASE as string | undefined) ?? null,
            p_environment: import.meta.env.MODE,
        });
    } catch {
        /* telemetry must never break the app — swallow; do NOT reportError here (would recurse) */
    }
}

export async function initObservability(): Promise<void> {
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
    const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
    const posthogHost =
        (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

    let sentry: any = null;
    let posthog: any = null;

    if (sentryDsn) {
        try {
            const Sentry = await import('@sentry/react');
            Sentry.init({
                dsn: sentryDsn,
                environment: import.meta.env.MODE,
                tracesSampleRate: 0.1,
                sendDefaultPii: false, // financial app — no PII by default
            });
            sentry = Sentry;
        } catch (err) {
            console.warn('[observability] VITE_SENTRY_DSN set but @sentry/react failed to load.', err);
        }
    }

    if (posthogKey) {
        try {
            const mod = await import('posthog-js');
            const ph = (mod.default ?? mod) as any;
            ph.init(posthogKey, {
                api_host: posthogHost,
                capture_pageview: true,
                autocapture: true,
                person_profiles: 'identified_only',
            });
            posthog = ph;
        } catch (err) {
            console.warn(
                '[observability] VITE_POSTHOG_KEY set but posthog-js failed to load. Run `npm i posthog-js`.',
                err,
            );
        }
    }

    setTelemetrySink({
        // Errors → Karr's own DB in production (always), console in dev, and Sentry
        // additionally when a DSN is configured.
        reportError: (error, context) => {
            if (import.meta.env.PROD) void logErrorToDb(error, context);
            else console.error('[telemetry] error:', error, context ?? '');
            sentry?.captureException(error, { extra: context });
        },
        // Funnel events → PostHog when configured; fall back to a Sentry message
        // so events aren't silently lost if only Sentry is on.
        reportEvent: (name, props) => {
            if (posthog) posthog.capture(name, props);
            else sentry?.captureMessage?.(name, { level: 'info', extra: props });
        },
        identify: (distinctId, traits) => {
            posthog?.identify(distinctId, traits);
            sentry?.setUser?.({
                id: distinctId,
                ...(traits?.email ? { email: String(traits.email) } : {}),
            });
        },
        reset: () => {
            posthog?.reset?.();
            sentry?.setUser?.(null);
        },
    });
}
